import React from "react";

import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  makeStyles,
  Button,
  List,
  ListItem,
  Avatar,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import { Theme } from "src/theme";
import Logo from "src/components/Logo";
import useAuth from "src/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { AccountInfo } from "@azure/msal-browser";
import { Person } from "@material-ui/icons";
import LetterAvatar from "src/components/LetterAvatar";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    display: "flex",
    minHeight: "100vh",
    flexDirection: "column",
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
    },
  },
  logoSection: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loginSection: {
    [theme.breakpoints.up("md")]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },

    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loginForm: {
    width: "80%",
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
  },
  logo: {
    width: "100%",
    height: "100%",
  },
  link: {
    width: "50%",
    height: "50%",
  },
  button: {
    marginTop: theme.spacing(5),
  },
  avatar: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.getContrastText(theme.palette.grey[200]),
  },
  list: {
    marginTop: theme.spacing(2),
  },
  icon: {
    marginRight: theme.spacing(2),
  },
}));

const RenewLoginView: React.FC = () => {
  const classes = useStyles();
  const { login, msalAuthAgent, activeAccount } = useAuth();
  const { t } = useTranslation();

  const allAccounts = msalAuthAgent?.getAllAccounts() || [];

  const handleClick = (account?: AccountInfo) => (): void => {
    login({ state: window.location.pathname, account });
  };

  const content = () => {
    // There are no known accounts
    if (allAccounts.length === 0)
      return [
        <Typography>{t("views.login.restrictedPage")}</Typography>,
        <Button
          className={classes.button}
          variant="contained"
          color="default"
          onClick={handleClick(undefined)}
        >
          {t("common.login")}
        </Button>,
      ];

    //There's only one account that is either already active or just known
    if (activeAccount != null || allAccounts.length === 1)
      return [
        <Typography variant="body2">
          {t("views.login.tokenExpired")}
        </Typography>,
        <Button
          className={classes.button}
          variant="contained"
          color="default"
          onClick={handleClick(activeAccount?.account ?? allAccounts[0])}
        >
          {t("common.renew")}
        </Button>,
      ];

    // There are multiple known accounts and the user has to choose
    return [
      <Typography variant="body2">
        {t("views.login.tokenExpired")} {t("views.login.noActiveAccount")}
      </Typography>,
      <List className={classes.list}>
        {allAccounts.map((account) => (
          <ListItem
            disableGutters
            key={account.username}
            button
            onClick={handleClick(account)}
          >
            <ListItemIcon className={classes.icon}>
              <LetterAvatar text={account.username} />
            </ListItemIcon>
            <ListItemText primary={account.username} />
          </ListItem>
        ))}
        <ListItem disableGutters button onClick={handleClick(undefined)}>
          <ListItemIcon className={classes.icon}>
            <Avatar className={classes.avatar}>
              <Person />
            </Avatar>
          </ListItemIcon>
          <ListItemText primary="Other" />
        </ListItem>
      </List>,
    ];
  };

  return (
    <div className={classes.root}>
      <Box className={classes.logoSection}>
        <RouterLink to="/" className={classes.link}>
          <Logo className={classes.logo} />
        </RouterLink>
      </Box>

      <Box className={classes.loginSection}>
        <Box className={classes.loginForm}>
          <Typography gutterBottom variant="h1">
            {t("views.login.title")}
          </Typography>
          {content()}
        </Box>
      </Box>
    </div>
  );
};

export default RenewLoginView;
